import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { DashboardDto } from '@thebb/shared';
import { get } from '../api';
import { useAuth } from '../auth';
import { Badge, BarChart, LineChart, Ring, Spinner, StatTile, fmtTime } from '../components/ui';

function greeting() {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Europe/Copenhagen' }).format(new Date()));
  return h < 10 ? 'Godmorgen' : h < 17 ? 'Goddag' : 'Godaften';
}

const dayLabel = (d: string) => ['Sø', 'Ma', 'Ti', 'On', 'To', 'Fr', 'Lø'][new Date(`${d}T12:00:00Z`).getUTCDay()]!;

export function DashboardPage() {
  const { me, can } = useAuth();
  const dash = useQuery({ queryKey: ['dashboard'], queryFn: () => get<DashboardDto>('/dashboard'), refetchInterval: 60_000, enabled: can('orders.view') });
  const d = dash.data;
  const today = new Intl.DateTimeFormat('da-DK', { timeZone: 'Europe/Copenhagen', dateStyle: 'full' }).format(new Date());
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {greeting()}, <span className="text-primary">{me?.name?.split(' ')[0] ?? ''}</span>
          </h1>
          <div className="mt-0.5 text-sm text-muted">Sådan ser dagen ud · {today}</div>
        </div>
        {d && (
          <div className="flex items-center gap-2 text-xs text-muted">
            <Badge tone={d.feed.lastOk === false ? 'red' : d.feed.enabled ? 'green' : 'zinc'}>3x-feed {d.feed.enabled ? (d.feed.lastOk === false ? 'fejl' : 'kører') : 'slået fra'}</Badge>
            <span>sidst hentet {fmtTime(d.feed.lastSuccessAt)}</span>
            <span>· {d.system.version}</span>
          </div>
        )}
      </div>

      {!can('orders.view') ? (
        <div className="card text-sm text-muted">Velkommen. Brug menuen til venstre.</div>
      ) : !d ? (
        <Spinner />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <StatTile label="Ordrer ind i dag" value={d.today.ordersIn} delta={d.today.ordersIn - d.today.ordersInYesterday} deltaLabel="vs. i går" />
            <StatTile label="Sendt i dag" value={d.today.shipped} delta={d.today.shipped - d.today.shippedYesterday} deltaLabel="vs. i går" />
            <StatTile label="Nye ordrer" value={d.open.newOrders} to={<Link to="/orders" search={{ status: 'new' } as never} className="text-primary hover:underline">Se ordrer</Link>} />
            <StatTile label="Skal tjekkes" value={d.open.needsCheck} tone="up-bad" to={<Link to="/orders" search={{ status: 'needs_check' } as never} className="text-primary hover:underline">Gennemgå</Link>} />
            <StatTile label="Til pluk" value={d.open.toPick} to={<Link to="/pick" className="text-primary hover:underline">Åbn pluk</Link>} />
            <StatTile label="Til pak" value={d.open.toPack} to={<Link to="/pack" className="text-primary hover:underline">Åbn pak</Link>} />
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="card xl:col-span-2">
              <div className="mb-1 flex items-center justify-between">
                <div>
                  <div className="text-base font-semibold">Ordrer ind og sendt</div>
                  <div className="text-xs text-muted">Pr. dag, de seneste 14 dage</div>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted">
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-primary" /> Ind</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-cyan" /> Sendt</span>
                </div>
              </div>
              <LineChart
                labels={d.days.map((x) => dayLabel(x.day))}
                series={[
                  { name: 'Ind', values: d.days.map((x) => x.ordersIn), color: '#1f3bb3', fill: '#a3c7f5' },
                  { name: 'Sendt', values: d.days.map((x) => x.shipped), color: '#52cdff', fill: '#d6f4ff' },
                ]}
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="rounded-2xl bg-primary p-5 text-white shadow-card">
                <div className="text-base font-semibold">Åbent lige nu</div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-white/70">Nye</div><div className="text-2xl font-semibold">{d.open.newOrders}</div></div>
                  <div><div className="text-white/70">Frigivet</div><div className="text-2xl font-semibold">{d.open.released}</div></div>
                  <div><div className="text-white/70">På hold</div><div className="text-2xl font-semibold">{d.open.onHold}</div></div>
                  <div><div className="text-white/70">Skal tjekkes</div><div className="text-2xl font-semibold">{d.open.needsCheck}</div></div>
                </div>
              </div>
              <div className="card">
                <div className="text-base font-semibold">Shops</div>
                <div className="mt-3 flex items-center justify-around">
                  <Ring percent={d.shops.continuous + d.shops.batch ? (d.shops.continuous / (d.shops.continuous + d.shops.batch)) * 100 : 0} color="#1f3bb3" label={<div><div className="text-xs text-muted">Løbende</div><div className="text-lg font-semibold">{d.shops.continuous}</div></div>} />
                  <Ring percent={d.shops.continuous + d.shops.batch ? (d.shops.batch / (d.shops.continuous + d.shops.batch)) * 100 : 0} color="#52cdff" label={<div><div className="text-xs text-muted">Efter lukning</div><div className="text-lg font-semibold">{d.shops.batch}</div></div>} />
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="card xl:col-span-2">
              <div className="mb-1 flex items-center justify-between">
                <div>
                  <div className="text-base font-semibold">Lager</div>
                  <div className="text-xs text-muted">{d.stock.variantsWithStock} varenumre med beholdning · {d.stock.onHand.toLocaleString('da-DK')} stk på lager · {d.stock.reserved.toLocaleString('da-DK')} reserveret</div>
                </div>
                <div className="text-xs text-muted">Navision: {d.stock.navisionItems.toLocaleString('da-DK')} varenumre, hentet {fmtTime(d.stock.navisionFetchedAt)}</div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <Ring percent={d.stock.onHand ? (d.stock.reserved / d.stock.onHand) * 100 : 0} color="#1f3bb3" size={64} label={<div><div className="text-xs text-muted">Reserveret af beholdning</div><div className="text-lg font-semibold">{d.stock.onHand ? Math.round((d.stock.reserved / d.stock.onHand) * 100) : 0}%</div></div>} />
                <Ring percent={d.stock.navisionItems ? (d.stock.variantsWithStock / d.stock.navisionItems) * 100 : 0} color="#52cdff" size={64} label={<div><div className="text-xs text-muted">Varenumre med beholdning her</div><div className="text-lg font-semibold">{d.stock.variantsWithStock.toLocaleString('da-DK')}</div></div>} />
                <Ring percent={d.feed.backfillDone ? 100 : 50} color="#34b1aa" size={64} label={<div><div className="text-xs text-muted">3x-historik</div><div className="text-lg font-semibold">{d.feed.backfillDone ? 'hentet' : 'henter…'}</div></div>} />
              </div>
              <div className="mt-4">
                <div className="mb-1 text-xs text-muted">Sendt pr. dag</div>
                <BarChart labels={d.days.map((x) => dayLabel(x.day))} series={[{ name: 'Sendt', values: d.days.map((x) => x.shipped), color: '#52cdff' }]} height={140} />
              </div>
            </div>
            <div className="card">
              <div className="mb-2 text-base font-semibold">Gaveshops der lukker snart</div>
              {d.shops.closingSoon.length === 0 ? (
                <div className="text-sm text-muted">Ingen gaveshops med åbne ordrer har en lukkedato endnu.</div>
              ) : (
                <ul className="divide-y divide-line text-sm">
                  {d.shops.closingSoon.map((s) => (
                    <li key={s.id} className="flex items-center justify-between py-2">
                      <Link to="/shops/$id" params={{ id: s.id }} className="font-medium text-ink hover:text-primary">{s.name}</Link>
                      <div className="text-right">
                        <div className="text-xs text-muted">lukker {s.closesAt}</div>
                        <div className="tabular-nums">{s.openOrders} ordrer</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
